import { useState } from 'react';
import { SketchPicker } from 'react-color';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingIndicator } from '../LoadingIndicator';
import { useSearchSymbols } from '../../hooks/api/use-symbols.hook';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import {
  useCreateTrimbleLayer,
  useUpdateTrimbleLayer,
  useGetTrimbleLayer,
} from '../../hooks/api/use-trimble-layers.hook';
import {
  TrimbleLayerApiModel,
  TrimbleLayerRelationship,
  WithLayer,
} from '../../../../types/models/trimble-layer.model';
import { TrimbleLayerForm } from './TrimbleLayerForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditTrimbleLayer = () => {
  const navigate = useNavigate();
  const { trimbleLayerId } = useParams<{ trimbleLayerId: string }>();
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState<HTMLElement | null>(null);

  const {
    trimbleLayer,
    loading: loadingAutodeskLayer,
    error: gettingError,
  } = useGetTrimbleLayer<TrimbleLayerApiModel & WithLayer>({
    trimbleLayerId: Number(trimbleLayerId),
    include: [TrimbleLayerRelationship.LAYER, TrimbleLayerRelationship.TEMPLATE, TrimbleLayerRelationship.SYMBOL],
  });
  const [createTrimbleLayer, { loading: creatingTrimbleLayer, error: createError }] = useCreateTrimbleLayer();
  const [updateTrimbleLayer, { loading: updatingTrimbleLayer, error: updateError }] = useUpdateTrimbleLayer();
  const { loading: loadingSymbols, response: symbolsResponse } = useSearchSymbols({});
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<TrimbleLayerForm>({
    resolver: classValidatorResolver(TrimbleLayerForm),
    values: trimbleLayer ? { ...trimbleLayer, ...(trimbleLayer.layer ?? {}) } : undefined,
  });

  const onSubmit: SubmitHandler<TrimbleLayerForm> = async (submittedValues) => {
    if (trimbleLayer) {
      const { symbol, template, ...values } = getDirtyValues(dirtyFields, submittedValues);
      await updateTrimbleLayer({
        ...values,
        description: values.description ?? '',
        symbolId: symbol?.id,
        templateId: template !== undefined ? (template?.id ?? null) : undefined,
        trimbleLayerId: trimbleLayer.id,
      });
    } else {
      const { symbol, template, ...values } = submittedValues;
      await createTrimbleLayer({
        ...values,
        description: values.description ?? '',
        symbolId: symbol.id,
        templateId: template?.id ?? null,
      });
    }

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    const isDisabled = creatingTrimbleLayer || updatingTrimbleLayer;

    return (
      <Box>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem', mb: 0 }}
          disabled={isDisabled}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <Controller
          control={control}
          name="template"
          render={({ field }) => (
            <Autocomplete
              {...register('template', { setValueAs: (value) => value || null })}
              loading={loadingTemplates}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(template) => template?.name ?? ''}
              options={templatesResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template"
                  error={Boolean(errors.template)}
                  helperText={errors.template?.message}
                />
              )}
            />
          )}
        />
        <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mr: '1rem' }}>
            Color:
          </Typography>
          <Controller
            control={control}
            name="color"
            render={({ field }) => (
              <>
                <Button
                  onClick={(e) => setColorPickerAnchorEl(e.currentTarget)}
                  disableRipple
                  sx={{
                    width: '100%',
                    height: 40,
                    backgroundColor: (field.value ?? '').includes('#') ? field.value : `#${field.value}`,
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    '&:hover': {
                      background: (field.value ?? '').includes('#') ? field.value : `#${field.value}`,
                      border: '1px solid rgba(0, 0, 0, 0.87)',
                    },
                  }}
                  variant="outlined"
                />
                <Popover
                  open={Boolean(colorPickerAnchorEl)}
                  anchorEl={colorPickerAnchorEl}
                  onClose={() => setColorPickerAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <SketchPicker
                    {...register('color')}
                    onChange={(color) => field.onChange(color.hex)}
                    color={field.value}
                    disableAlpha
                  />
                </Popover>
              </>
            )}
          />
        </Box>
        <Controller
          control={control}
          name="symbol"
          render={({ field }) => (
            <Autocomplete
              {...register('symbol')}
              loading={loadingSymbols}
              sx={{ mt: 2 }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(symbol) => symbol?.name ?? ''}
              options={symbolsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Linetype"
                  error={Boolean(errors.symbol)}
                  helperText={errors.symbol?.message}
                />
              )}
            />
          )}
        />
        <TextField
          {...register('lineweight', { valueAsNumber: true })}
          type="number"
          variant="outlined"
          sx={{ mt: 2 }}
          disabled={creatingTrimbleLayer || updatingTrimbleLayer}
          error={Boolean(errors.lineweight)}
          helperText={errors.lineweight?.message}
          label="Lineweight (mm)"
          margin="dense"
          size="small"
          fullWidth
        />
        <FormControlLabel
          sx={{ ml: 0, mt: 0 }}
          label="Protected:"
          labelPlacement="start"
          control={
            <Controller
              control={control}
              name="isProtected"
              render={({ field }) => <Checkbox {...register('isProtected')} checked={Boolean(field.value)} />}
            />
          }
        />
        <TextField
          {...register('displayPriority', { valueAsNumber: true })}
          type="number"
          variant="outlined"
          sx={{ mt: 0.5 }}
          disabled={creatingTrimbleLayer || updatingTrimbleLayer}
          error={Boolean(errors.displayPriority)}
          helperText={errors.displayPriority?.message}
          label="Display Priority"
          margin="dense"
          size="small"
          fullWidth
        />
        <FormControlLabel
          sx={{ ml: 0 }}
          label="Print:"
          labelPlacement="start"
          control={
            <Controller
              control={control}
              name="isPrint"
              render={({ field }) => <Checkbox {...register('isPrint')} checked={Boolean(field.value)} />}
            />
          }
        />
        <TextField
          {...register('description')}
          variant="outlined"
          sx={{ mt: 1 }}
          disabled={isDisabled}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          label="Description"
          margin="dense"
          size="small"
          fullWidth
          multiline
          rows={4}
        />
      </Box>
    );
  };

  if (loadingAutodeskLayer) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingAutodeskLayer ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
          <DialogTitle>{`${trimbleLayer ? 'Edit' : 'Add'} Trimble Layer`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {trimbleLayer ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
